/**
 * @module lib/election/helpers
 * @description Helper functions for extracting specific ridings and candidates
 * from the election ridings JSON data, and for sorting ridings, etc.
 */

import { getProp } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[ELECTION-HELPERS]";

/**
 * Regions and the provinces that belong to them, etc.
 */
export const regions = {
  "atlantic": {
    displayName: "Atlantic",
    provinces: {
      "nl": { id: 1, displayName: "Newfoundland" },
      "pe": { id: 2, displayName: "Prince Edward Island" },
      "ns": { id: 3, displayName: "Nova Scotia" },
      "nb": { id: 4, displayName: "New Brunswick" }
    }
  },
  "quebec": {
    displayName: "Quebec",
    provinces: {
      "qc": { id: 5, displayName: "Quebec" }
    }
  },
  "ontario": {
    displayName: "Ontario",
    provinces: {
      "on": { id: 6, displayName: "Ontario" }
    }
  },
  "prairies": {
    displayName: "Prairies",
    provinces: {
      "mb": { id: 7, displayName: "Manitoba" },
      "sk": { id: 8, displayName: "Saskatchewan" },
      "ab": { id: 9, displayName: "Alberta" }
    }
  },
  "bc": {
    displayName: "British Columbia",
    provinces: {
      "bc": { id: 10, displayName: "British Columbia" }
    }
  },
  "north": {
    displayName: "The North",
    provinces: {
      "yt": { id: 11, displayName: "Yukon" },
      "nt": { id: 12, displayName: "Northwest Territories" },
      "nu": { id: 13, displayName: "Nunavut" }
    }
  }
};

/**
 * A mapping of abbreviated party names to CSS class name fragments and full display names.
 * In 2019, "PPC" was returned as "PPC" in the CP feed. In 2021, "PP".
 */
export const parties = {
  "LIB": {
    className: "lib",
    displayName: "Liberal"
  },
  "CON": {
    className: "cpc",
    displayName: "Conservative"
  },
  "NDP": {
    className: "ndp",
    displayName: "New Democrat"
  },
  "BQ": {
    className: "bq",
    displayName: "Bloc Québécois"
  },
  "GRN": {
    className: "gp",
    displayName: "Green"
  },
  "PP": {
    className: "ppc",
    displayName: "People's"
  },
  "PPC": {
    className: "ppc",
    displayName: "People's"
  },
  "OTH": {
    className: "other",
    displayName: "Others"
  },
  "APP": {
    className: "other",
    displayName: "Animal Protection Party of Canada"
  },
  "CHP": {
    className: "other",
    displayName: "Christian Heritage Party"
  },
  "COM": {
    className: "other",
    displayName: "Communist Party of Canada"
  },
  "LTN": {
    className: "other",
    displayName: "Libertarian Party of Canada"
  },
  "MP": {
    className: "other",
    displayName: "Marijuana Party"
  },
  "ML": {
    className: "other",
    displayName: "Marxist-Leninist Party of Canada"
  },
  "NCA": {
    className: "other",
    displayName: "National Citizens Alliance of Canada"
  },
  "RP": {
    className: "other",
    displayName: "Parti Rhinoceros Party"
  },
  "PCP": {
    className: "other",
    displayName: "Progressive Canadian Party"
  },
  "IND": {
    className: "other",
    displayName: "Independent"
  },
  "NA": {
    className: "other",
    displayName: "Non-Affiliated"
  },
  "CFF": {
    className: "other",
    displayName: "Canada's Fourth Front"
  },
  "CNP": {
    className: "other",
    displayName: "Canadian Nationalist Party"
  },
  "PIQ": {
    className: "other",
    displayName: "Parti pour L'Indépendance du Québec"
  },
  "SCC": {
    className: "other",
    displayName: "Stop Climate Change"
  },
  "UPC": {
    className: "other",
    displayName: "The United Party of Canada"
  },
  "VCP": {
    className: "other",
    displayName: "Veterans Coalition Party of Canada"
  },
  "CCF": {
    className: "other",
    displayName: "Co-operative Commonwealth Federation"
  }
};

/**
 * Extract all of the key ridings from the larger array
 * @param {Array<Object>} ridings - An array of riding objects
 * @param {Number} id - A riding ID
 * @returns {Object|undefined} The matching riding or undefined
 */
export function getRidingByID(ridings, id) {
  return ridings.find(r => Number(r.id) === id);
}

/**
 * Extract all of the key ridings from the larger array
 * @param {Array<Object>} ridings - An array of riding objects
 * @returns {Array<Object>} An array of key ridings
 */
export function getKeyRidings(ridings) {
  return ridings.filter(r => r.keyRiding);
}

/**
 * Get ridings that belong to a particular region
 * @param {Array<Object>} ridings - An array of riding objects
 * @param {String} regionID - The ID of a particular region
 * @returns {Array<Object>} An array of ridings in provinces within the given region
 */
export function getRidingsByRegion(ridings, regionID) {
  const provincesInRegion = regions[regionID].provinces;
  const ridingsByProvince = Object.values(provincesInRegion).map(province => {
    return getRidingsByProvince(ridings, province.id);
  });
  return [].concat(...ridingsByProvince);
}

/**
 * Get ridings that belong to a particular province
 * @param {Array<Object>} ridings - An array of riding objects
 * @param {Number} provinceID - The ID of a particular province
 * @returns {Array<Object>} An array of ridings in the given province
 */
export function getRidingsByProvince(ridings, provinceID) {
  return ridings.filter(r => Number(r.RegionID) === provinceID);
}

/**
 * Sort ridings by province ID, in ascending order
 * @param {Array<Object>} ridings - An array of riding objects
 * @returns {Array<Object>} An array of ridings, sorted by province ID
 */
export function sortRidingsByProvince(ridings) {
  return ridings.map(r => r).sort((a, b) => {
    return Number(a.RegionID) - Number(b.RegionID);
  });
}

/**
 * Sort ridings by name, in alphabetical order
 * @param {Array<Object>} ridings - An array of riding objects
 * @returns {Array<Object>} An array of ridings, sorted alphabetically by name
 */
export function sortRidingsByName(ridings) {
  return ridings.map(r => r).sort((a, b) => {
    const nameA = a.RNE ? a.RNE.trim().toUpperCase() : "";
    const nameB = b.RNE ? b.RNE.trim().toUpperCase() : "";
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
}

/**
 * Get the incumbent from the list of candidates in a riding
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Object|null} The riding's incumbent candidate
 */
export function getIncumbentCandidate(candidates) {
  if (candidates && candidates.length) {
    return candidates.find(c => {
      if (!c.Status || typeof c.Status !== "string") {
        return false;
      }
      if (c.Status.toLowerCase().trim() === "member") {
        return true;
      }
      return false;
    });
  }
  return null;
}

/**
 * Get the leading candidate from the list of candidates in a riding
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Object|null} The riding's leading candidate (i.e. the one with the most votes)
 */
export function getLeadingCandidate(candidates) {
  const sorted = sortCandidatesByVotes(candidates);
  return sorted && sorted[0] && sorted[0].V > 0 ? sorted[0] : null;
}

/**
 * Get the elected candidate from the list of candidates in a riding
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Object|null} The riding's elected candidate (i.e. the winner)
 */
export function getElectedCandidate(candidates) {
  if (candidates && candidates.length) {
    return candidates.find(c => {
      if (c.E && typeof c.E === "string" && c.E.toLowerCase().trim() === "yes") {
        return true;
      }
      return false;
    });
  }
  return null;
}

/**
 * Sort a riding's candidates by the number of votes that they've received
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Array|null} An array of candidates, sorted by most votes, in descending order
 */
export function sortCandidatesByVotes(candidates) {
  if (candidates && candidates.length) {
    return candidates.map(c => c).sort((a, b) => {
      const aVotes = a.V ? Number(a.V) : 0;
      const bVotes = b.V ? Number(b.V) : 0;
      return bVotes - aVotes;
    });
  }
  return null;
}

/**
 * Sort a riding's candidates by the default party order
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Array|null} An array of candidates, sorted by party
 */
export function sortCandidatesByParty(candidates) {
  if (candidates && candidates.length) {
    const parties = ["LIB", "CON", "NDP", "BQ", "GRN", "PPC", "PP"];

    return candidates.map(c => c).sort((a, b) => {
      const indexA = parties.indexOf(a.PE);
      const indexB = parties.indexOf(b.PE);
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }
      return indexA - indexB;
    });
  }
  return null;
}

/**
 * Get the number of votes that have been cast in a particular riding
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Number} The number of votes
 */
export function getTotalCountedVotesInRiding(candidates) {
  if (candidates && candidates.length) {
    return candidates.reduce((accumulator, c) => {
      const votes = c.V ? Number(c.V) : 0;
      return accumulator + votes;
    }, 0);
  }
  return 0;
}

/**
 * Get specific information about a particular candidate
 * @param {Object} candidate - A single riding candidate object
 * @returns {Object} Formatted information about the given candidate
 */
export function getCandidateInfo(candidate) {
  const existsInMap = getProp(candidate, "PE") && parties[candidate.PE];
  return {
    id: getProp(candidate, "ID"),
    partyAbbrName: getProp(candidate, "PE", "OTH"),
    partyDisplayName: existsInMap ? parties[candidate.PE].displayName : parties.OTH.displayName,
    partyClassName: existsInMap ? parties[candidate.PE].className : parties.OTH.className,
    name: getProp(candidate, "FN") && getProp(candidate, "LN") ? `${candidate.FN} ${candidate.LN}` : "N/A",
    votes: getProp(candidate, "V") ? Number(candidate.V) : 0
  };
}

/**
 * Get the numer of votes that each party has received in a particular riding
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Array<Object>|null} An array of objects containing each party's
 * abbreviated name, CSS class name, display name, and the number of votes that
 * their candidate has received
 */
export function getVotesForAllParties(candidates) {
  if (candidates && candidates.length) {
    return candidates.map(c => getCandidateInfo(c)).sort((a, b) => b.votes - a.votes);
  }
  return null;
}

/**
 * Get the numer of votes that the top parties have received in a particular riding
 * @param {Array<Object>} candidates - An array of candidate objects
 * @returns {Array<Object>|null} An array of objects containing each party's
 * abbreviated name, CSS class name, display name, and the number of votes that
 * their candidate has received
 */
export function getVotesForTopParties(candidates) {
  const votesForAllParties = getVotesForAllParties(candidates);
  if (!votesForAllParties) {
    return null;
  }
  const votesForTopParties = votesForAllParties.filter(item => {
    return item.partyClassName !== parties.OTH.className;
  });
  const votesForOtherParties = votesForAllParties.filter(item => {
    return item.partyClassName === parties.OTH.className;
  });
  const totalVotesForOtherParties = votesForOtherParties.reduce((accumulator, item) => {
    return accumulator + item.votes;
  }, 0);

  votesForTopParties.push({
    partyAbbrName: "OTH",
    partyDisplayName: parties.OTH.displayName,
    partyClassName: parties.OTH.className,
    name: "N/A",
    votes: totalVotesForOtherParties
  });
  return votesForTopParties.sort((a, b) => b.votes - a.votes);
}

/**
 * Get the percentage of votes that each party has received in a given riding,
 * based on the number of votes that have been cast (as opposed to the number
 * of votes that could possibly be cast)
 * @param {Object} riding - A single riding object
 * @returns {Array<Object>|null} An array of objects containing each party's
 * abbreviated name, CSS class name, display name, the number of votes that
 * their candidate has received, and the percentage of votes that their
 * candidate has received
 */
export function getVotePercentagesForAllParties(riding) {
  const votesForAllParties = getVotesForAllParties(riding.Cand);
  const totalCountedVotes = getTotalCountedVotesInRiding(riding.Cand);

  if (logger.debugEnabled) {
    debugGetVotePercentagesForAllParties(riding);
  }
  if (votesForAllParties) {
    return votesForAllParties.map(item => {
      const percentOfCountedVotes = item.votes / totalCountedVotes * 100;
      return Object.assign({
        votePercentage: percentOfCountedVotes
      }, item);
    });
  }
  return null;
}

/**
 * Log detailed information about a given riding's votes, for debugging purposes
 * @param {Object} riding - A single riding object
 */
function debugGetVotePercentagesForAllParties(riding) {
  const totalPossibleVotes = riding.TotalVoters;
  const totalCountedVotes = getTotalCountedVotesInRiding(riding.Cand);
  const totalPercentOfCountedVotes = Math.round(totalCountedVotes / totalPossibleVotes * 100);
  const votesForAllParties = getVotesForAllParties(riding.Cand);

  logger.info(`${debugTag} Total possible votes: ${totalPossibleVotes}`);
  logger.info(`${debugTag} Total counted votes: ${totalCountedVotes}`);
  logger.info(`${debugTag} Total percent of counted votes: ${totalPercentOfCountedVotes}% (counted / possible * 100)`);

  logger.groupCollapsed(`${debugTag} Counted votes for each party`);
  votesForAllParties.forEach(item => {
    logger.info(`${debugTag} ${item.partyAbbrName}: ${item.votes}`);
  });
  logger.groupEnd(`${debugTag} Counted votes for each party`);

  logger.groupCollapsed(`${debugTag} Percent of total possible votes for each party`);
  votesForAllParties.forEach(item => {
    logger.info(`${debugTag} ${item.partyAbbrName}: ${item.votes / totalPossibleVotes * 100}%`);
  });
  logger.groupEnd(`${debugTag} Percent of total possible votes for each party`);

  logger.groupCollapsed(`${debugTag} Percent of total counted votes for each party`);
  votesForAllParties.forEach(item => {
    logger.info(`${debugTag} ${item.partyAbbrName}: ${item.votes / totalCountedVotes * 100}%`);
  });
  logger.groupEnd(`${debugTag} Percent of total counted votes for each party`);
}


/**
 * @method getNumDifference
 * @description Calculate the difference between gain and loss.
 * @param {Object} party all party data.
 * @returns {Number} difference.
 */
export function getNumDifference(party) {
  const gain = Number(getProp(party, "Gain", 0));
  const loss = Number(getProp(party, "Loss", 0));
  return 0 + gain - loss;
}

/**
 * @method getParty
 * @param {String} party name from API response.
 * @param {String} electionYear election year whose results are being returned
 * @description Get the party's full name, class modifier and previousElection results.
 * @returns {Object}
 */
export function getParty(party, electionYear) {
  if (electionYear === "2019") {
    switch (party) {
      case "lib":
        return {
          party: "Liberal",
          partyClassModifier: "lib",
          previousElection: 177
        };
      case "con":
        return {
          party: "Conservative",
          partyClassModifier: "cpc",
          previousElection: 95
        };
      case "ndp":
        return {
          party: "NDP",
          partyClassModifier: "ndp",
          previousElection: 39
        };
      case "bq":
        return {
          party: "Bloc Québécois",
          partyClassModifier: "bq",
          previousElection: 10
        };
      case "grn":
        return {
          party: "Green",
          partyClassModifier: "gp",
          previousElection: 2
        };
      case "ppc":
        return {
          party: "People's",
          partyClassModifier: "ppc",
          previousElection: 1
        };
      case "others":
        return {
          party: "Others",
          partyClassModifier: "grey",
          previousElection: 14
        };
      default:
        return {};
    }
  } else {
    switch (party) {
      case "lib":
        return {
          party: "Liberal",
          partyClassModifier: "lib",
          previousElection: 157
        };
      case "con":
        return {
          party: "Conservative",
          partyClassModifier: "cpc",
          previousElection: 121
        };
      case "ndp":
        return {
          party: "NDP",
          partyClassModifier: "ndp",
          previousElection: 24
        };
      case "bq":
        return {
          party: "Bloc Québécois",
          partyClassModifier: "bq",
          previousElection: 32
        };
      case "grn":
        return {
          party: "Green",
          partyClassModifier: "gp",
          previousElection: 3
        };
      case "pp":
        return {
          party: "People's",
          partyClassModifier: "ppc",
          previousElection: 0
        };
      case "others":
        return {
          party: "Others",
          partyClassModifier: "grey",
          previousElection: 1
        };
      default:
        return {};
    }
  }
}
